// hooks/useScreenState.ts
// Manages the 7 shared state variables for any list screen
// Q-187: Screen Administrator

import { useState, useCallback, useMemo } from 'react';
import type { ScreenDefinition, ScreenState } from '@/types/screen-definition';

export function useScreenState(definition: ScreenDefinition): ScreenState {
  const [search, setSearchRaw] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeRaw] = useState(definition.pagination.defaultPageSize);
  const [sort, setSortRaw] = useState<string | undefined>(definition.defaultSort?.field);
  const [order, setOrderRaw] = useState<'asc' | 'desc'>(definition.defaultSort?.order ?? 'desc');
  const [quickFilterValues, setQuickFilterValuesRaw] = useState<Record<string, string[]>>({});
  const [filterValues, setFilterValuesRaw] = useState<Record<string, unknown>>({});

  // Auto-reset page to 1 when search/filters change
  const setSearch = useCallback((value: string) => {
    setSearchRaw(value);
    setPage(1);
  }, []);

  const setPageSize = useCallback((size: number) => {
    setPageSizeRaw(size);
    setPage(1);
  }, []);

  const setSort = useCallback((field: string, newOrder: 'asc' | 'desc') => {
    setSortRaw(field);
    setOrderRaw(newOrder);
  }, []);

  const setQuickFilterValues = useCallback((key: string, values: string[]) => {
    setQuickFilterValuesRaw((prev) => ({ ...prev, [key]: values }));
    setPage(1);
  }, []);

  const setFilterValues = useCallback((values: Record<string, unknown>) => {
    setFilterValuesRaw(values);
    setPage(1);
  }, []);

  // Pre-built query params for server-side pagination
  const queryParams = useMemo(() => ({
    search: search || undefined,
    page,
    page_size: pageSize,
    sort_by: sort,
    sort_order: sort ? order : undefined,
  }), [search, page, pageSize, sort, order]);

  return {
    search,
    page,
    pageSize,
    sort,
    order,
    quickFilterValues,
    filterValues,
    setSearch,
    setPage,
    setPageSize,
    setSort,
    setQuickFilterValues,
    setFilterValues,
    queryParams,
  };
}
